import {
  CheckCircleBrokenIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XIcon,
} from '@langchain/untitled-ui-icons';
import { CircularProgress, Textarea } from '@mui/joy';

import { ReactNode, useMemo, useRef, useState } from 'react';

import { FeedbackChip } from '@/components/FeedbackChips';
import { IconButton } from '@/design-system/components/IconButton';
import { Input } from '@/design-system/components/Input';
import { FeedbackConfigSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

/* -------------------------------------------------------------------------- */
/*  Expandable container (used for rubric items & instructions)               */
/* -------------------------------------------------------------------------- */

interface ExpandableComponentProps {
  minimizedComponent?: ReactNode;
  expandedComponent: ReactNode;
  headerComponent?: ReactNode;
  collapsedHeaderComponent?: ReactNode;
  defaultExpanded?: boolean;
  overrideExpanded?: boolean;
  overrideSetExpanded?: (expanded: boolean) => void;
  className?: string;
}

function ExpandableComponent({
  minimizedComponent,
  expandedComponent,
  headerComponent,
  collapsedHeaderComponent,
  defaultExpanded,
  overrideExpanded,
  overrideSetExpanded,
  className,
}: ExpandableComponentProps) {
  const [expandedState, setExpandedState] = useState(!!defaultExpanded);

  const expanded = overrideExpanded != null ? overrideExpanded : expandedState;
  const setExpanded = overrideSetExpanded ?? setExpandedState;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <button
        type="button"
        className="flex cursor-pointer items-center justify-between gap-2"
        onClick={() => setExpanded(!expanded)}
      >
        {collapsedHeaderComponent && !expanded
          ? collapsedHeaderComponent
          : headerComponent}
        {expanded ? (
          <ChevronDownIcon className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 shrink-0" />
        )}
      </button>
      {expanded ? expandedComponent : minimizedComponent}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Free-form text feedback input                                             */
/* -------------------------------------------------------------------------- */

interface SingleFreeformTextInputProps {
  value: string | null;
  autoFocus?: boolean;
  feedbackConfig: FeedbackConfigSchema; // kept for parity / future use
  onChange: (value: string) => void;
  onDeleted: () => void;
}

function SingleFreeformTextInput({
  value,
  autoFocus,
  onChange,
  onDeleted,
}: SingleFreeformTextInputProps) {
  const [currentValue, setCurrentValue] = useState<string | null>(value);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <div className="flex-1">
          <Textarea
            placeholder="Enter text"
            value={currentValue ?? ''}
            onChange={(event) => setCurrentValue(event.target.value)}
            autoFocus={autoFocus ?? false}
            onKeyDown={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (event.key === 'Enter' && event.metaKey) {
                event.currentTarget.blur();
              }
            }}
            onBlur={() => {
              if (currentValue != null && currentValue !== '') {
                onChange(currentValue);
              } else {
                onDeleted();
              }
            }}
          />
        </div>
        <IconButton
          icon={XIcon}
          label="Delete"
          variant="plain"
          color="secondary"
          size="sm"
          onClick={() => {
            onDeleted();
            setCurrentValue(null);
          }}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Free-form numeric feedback input                                          */
/* -------------------------------------------------------------------------- */

interface SingleFreeformNumberInputProps {
  value?: number;
  autoFocus?: boolean;
  feedbackConfig: FeedbackConfigSchema;
  onChange: (value: number) => void;
  onDeleted: () => void;
}

function SingleFreeformNumberInput({
  value,
  autoFocus,
  feedbackConfig,
  onChange,
  onDeleted,
}: SingleFreeformNumberInputProps) {
  const [currentValue, setCurrentValue] = useState<number | null | undefined>(
    value
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const minMaxText = useMemo(() => {
    const { min, max } = feedbackConfig.feedback_config;
    if (min != null && max != null) return `Min: ${min}, Max: ${max}`;
    if (min != null) return `Min: ${min}`;
    if (max != null) return `Max: ${max}`;
    return null;
  }, [feedbackConfig.feedback_config]);

  return (
    <div className="flex flex-col gap-2">
      {minMaxText && (
        <div className="text-xs text-quaternary">{minMaxText}</div>
      )}
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            autoFocus={autoFocus ?? false}
            ref={inputRef}
            value={currentValue != null ? String(currentValue) : ''}
            onChange={(val) => {
              const num = val === '' ? undefined : Number(val);
              setCurrentValue(
                isNaN(num as number) ? undefined : (num as number)
              );
            }}
            placeholder="Enter score"
            type="number"
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') event.currentTarget.blur();
            }}
            onBlur={() => {
              if (currentValue != null && !isNaN(currentValue)) {
                onChange(currentValue);
              } else {
                onDeleted();
              }
            }}
          />
        </div>
        <IconButton
          icon={XIcon}
          label="Delete"
          variant="plain"
          color="secondary"
          size="sm"
          onClick={() => {
            onDeleted();
            setCurrentValue(null);
          }}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Category choice button                                                    */
/* -------------------------------------------------------------------------- */

interface SingleFeedbackCategoryChoiceProps {
  description?: string;
  categoryName: string;
  selected: boolean;
  onSelect: () => void;
  loading?: boolean;
}

function SingleFeedbackCategoryChoice({
  description,
  categoryName,
  selected,
  onSelect,
  loading,
}: SingleFeedbackCategoryChoiceProps) {
  return (
    <button
      type="button"
      className={cn(
        'flex w-full cursor-pointer items-center gap-2 border border-transparent p-1',
        selected ? 'rounded-md border-secondary bg-brand-tertiary' : ''
      )}
      onClick={onSelect}
    >
      <div
        className={cn(
          'relative flex items-center rounded-md border border-transparent px-2 py-1.5 text-xs',
          selected ? 'bg-brand-primary' : ' border-primary'
        )}
      >
        <span
          className={cn(
            loading && 'opacity-0',
            'text-primary',
            selected && 'invert'
          )}
        >
          {categoryName}
        </span>

        {loading && (
          <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center">
            <CircularProgress
              sx={{
                '--CircularProgress-size': '14px',
                '--CircularProgress-progressThickness': '2px',
                '--CircularProgress-trackThickness': '2px',
                transform: 'scaleX(-1)',
              }}
            />
          </div>
        )}
      </div>
      <div className="line-clamp-1 flex-1 whitespace-normal break-all text-left text-xs">
        {description}
      </div>
    </button>
  );
}

const FeedbackHeader = ({
  feedbackKey,
  feedback,
  isMutating,
  preview,
}: {
  feedbackKey: string;
  feedback?: { value?: string; score?: number };
  isMutating: boolean;
  preview?: string;
}) => {
  return (
    <div className="flex w-full items-center justify-between pr-2">
      <div className="flex items-center gap-1">
        <FeedbackChip
          feedback_key={feedbackKey}
          loading={isMutating}
          value={feedback?.value ?? feedback?.score}
        />
        {preview && (
          <div className="mx-2 line-clamp-1 overflow-ellipsis whitespace-normal break-all text-sm text-disabled">
            {preview}
          </div>
        )}
      </div>
      {feedback && (
        <CheckCircleBrokenIcon className="h-4 w-4 shrink-0 text-ls-green-600" />
      )}
    </div>
  );
};

export {
  ExpandableComponent,
  FeedbackHeader,
  SingleFeedbackCategoryChoice,
  SingleFreeformNumberInput,
  SingleFreeformTextInput,
};
